import { Crop } from "react-image-crop";

export interface ImageSize {
  width: number;
  height: number;
}

interface FlipOptions {
  horizontal: boolean;
  vertical: boolean;
}

export const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous"); // needed to avoid cross-origin issues on CodeSandbox
    image.src = url;
  });

export function getRadianAngle(degreeValue: number): number {
  return (degreeValue * Math.PI) / 180;
}

/**
 * Returns the new bounding area of a rotated rectangle.
 */
export function rotateSize(
  width: number,
  height: number,
  rotation: number,
): ImageSize {
  const rotRad = getRadianAngle(rotation);

  return {
    width:
      Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

/**
 * This function was adapted from the one in the ReadMe of https://github.com/DominicTobias/react-image-crop
 * You can provide a transform function to decide how you want to transform the blob. By default, it will return a base64 URL.
 * @see {@link getCroppedImgAsFile}, a convenience function that will return a File object
 */
export async function getCroppedImg<OutType = string>(
  imageSrc: string,
  pixelCrop: Crop,
  rotation = 0,
  displayedImageSize?: ImageSize,
  flip: FlipOptions = { horizontal: false, vertical: false },
  transform: (blob: Blob) => OutType = URL.createObjectURL as (
    blob: Blob,
  ) => OutType,
): Promise<OutType> {
  const image = await createImage(imageSrc);

  const scaledWidth = displayedImageSize
    ? displayedImageSize.width
    : image.width;
  const scaledHeight = displayedImageSize
    ? displayedImageSize.height
    : image.height;

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) throw new Error("Failed to get canvas context");

  const rotRad = getRadianAngle(rotation);

  // calculate bounding box of the rotated image
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
    scaledWidth,
    scaledHeight,
    rotation,
  );

  // set canvas size to match the bounding box
  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  // translate canvas context to a central location to allow rotating and flipping around the center
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
  ctx.translate(-scaledWidth / 2, -scaledHeight / 2);

  ctx.drawImage(image, 0, 0, scaledWidth, scaledHeight);

  const croppedCanvas = document.createElement("canvas");

  const croppedCtx = croppedCanvas.getContext("2d");

  if (!croppedCtx) throw new Error("Failed to get cropped canvas context");

  const cropX = pixelCrop.x;
  const cropY = pixelCrop.y;
  const cropWidth = pixelCrop.width;
  const cropHeight = pixelCrop.height;

  croppedCanvas.width = cropWidth;
  croppedCanvas.height = cropHeight;

  croppedCtx.drawImage(
    canvas,
    cropX,
    cropY,
    cropWidth,
    cropHeight,
    0,
    0,
    cropWidth,
    cropHeight,
  );

  return new Promise((resolve) => {
    croppedCanvas.toBlob((file) => {
      if (!file) throw new Error("Failed to convert cropped canvas to blob");
      resolve(transform(file));
    }, "image/png");
  });
}

export async function getCroppedImgAsFile(
  imageSrc: string,
  pixelCrop: Crop,
  displayedImageSize: ImageSize,
  rotation = 0,
  flip: FlipOptions = { horizontal: false, vertical: false },
) {
  return getCroppedImg(
    imageSrc,
    pixelCrop,
    rotation,
    displayedImageSize,
    flip,
    (file) => new File([file], "croppedImage.jpg"),
  );
}

export async function getRotatedImage(
  imageSrc: string,
  rotation = 0,
): Promise<string | null> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    return null;
  }

  const orientationChanged =
    rotation === 90 ||
    rotation === -90 ||
    rotation === 270 ||
    rotation === -270;
  if (orientationChanged) {
    canvas.width = image.height;
    canvas.height = image.width;
  } else {
    canvas.width = image.width;
    canvas.height = image.height;
  }

  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.drawImage(image, -image.width / 2, -image.height / 2);

  return new Promise((resolve) => {
    canvas.toBlob((file) => {
      if (file) {
        resolve(URL.createObjectURL(file));
      } else {
        resolve(null);
      }
    }, "image/png");
  });
}
